import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Plus, Pencil, Trash2, Power, PowerOff, Megaphone, ChevronRight } from "lucide-react";

/**
 * A19-P6.4 — Admin internal Ad-slot management.
 *
 * Path: /admin/ad-slots
 * Roles: admin / staff only.
 *
 *  • List + filter slots from `ad_slots` (Mongo).
 *  • Create / edit via right-side drawer with live banner preview.
 *  • Toggle active / soft-delete (reversible by admin via DB).
 *
 * Strictly internal — never external ads, never paid placements,
 * never tracking. Free-text fields are server-validated to reject
 * HTML angle brackets.
 */

const PLACEMENTS = ["referrals_top", "dashboard_home", "dashboard_nav"];
const CTA_TARGETS = ["composer", "referrals_page", "questionnaires", "campaigns"];
const STYLE_VARIANTS = ["emerald", "violet", "sky", "amber"];
const PROFILE_OPTIONS = [
  "", "player", "parent_guardian", "coach", "club_director",
  "federation_manager", "fitness_coach", "sports_doctor",
  "physiotherapist", "sport_psychologist",
];

const VARIANT_RAIL = {
  emerald: "bg-emerald-500", violet: "bg-violet-500",
  sky: "bg-sky-500", amber: "bg-amber-500",
};
const VARIANT_TAG = {
  emerald: "bg-emerald-100 text-emerald-900",
  violet:  "bg-violet-100 text-violet-900",
  sky:     "bg-sky-100 text-sky-900",
  amber:   "bg-amber-100 text-amber-900",
};
const VARIANT_CTA = {
  emerald: "bg-emerald-600 hover:bg-emerald-700",
  violet:  "bg-violet-600 hover:bg-violet-700",
  sky:     "bg-sky-600 hover:bg-sky-700",
  amber:   "bg-amber-600 hover:bg-amber-700",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric", month: "short", day: "2-digit",
    }).format(new Date(iso));
  } catch { return String(iso).slice(0, 10); }
};

// ISO datetime <-> <input type="datetime-local"> "YYYY-MM-DDTHH:mm"
const isoToLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};
const localToIso = (loc) => {
  if (!loc) return "";
  try { return new Date(loc).toISOString(); } catch { return loc; }
};

const slotStatus = (slot, t) => {
  if (!slot.active) return { key: "inactive", label: t("referrals.adminAdSlots.status.inactive"), cls: "bg-slate-200 text-slate-700" };
  const now = Date.now();
  const s = new Date(slot.starts_at).getTime();
  const e = new Date(slot.ends_at).getTime();
  if (now < s) return { key: "upcoming", label: t("referrals.adminAdSlots.status.upcoming"), cls: "bg-sky-100 text-sky-800" };
  if (now > e) return { key: "expired", label: t("referrals.adminAdSlots.status.expired"), cls: "bg-amber-100 text-amber-900" };
  return { key: "active", label: t("referrals.adminAdSlots.status.active"), cls: "bg-emerald-100 text-emerald-800" };
};

const emptyForm = () => {
  const now = new Date();
  const inAYear = new Date();
  inAYear.setFullYear(inAYear.getFullYear() + 1);
  return {
    slot_id: "",
    placement: "referrals_top",
    title_text: "", title_key: "",
    body_text: "", body_key: "",
    cta_text: "", cta_key: "",
    cta_target: "composer",
    style_variant: "emerald",
    target_profile: "",
    target_country: "",
    target_language: "",
    priority: 10,
    starts_at: isoToLocal(now.toISOString()),
    ends_at: isoToLocal(inAYear.toISOString()),
    active: true,
  };
};

// Resolve a slot's user-facing strings using the same logic the public
// AdSlotBanner uses — prefer `*_text`, fall back to `t(*_key)`.
const resolveText = (t, valKey, valText) => {
  if (valText && valText.trim()) return valText;
  if (!valKey) return "";
  const out = t(valKey);
  if (!out || out === valKey) return "";
  return out;
};

const SlotPreview = ({ form, t }) => {
  const title = resolveText(t, form.title_key, form.title_text);
  const body  = resolveText(t, form.body_key, form.body_text);
  const cta   = resolveText(t, form.cta_key, form.cta_text);
  const variant = form.style_variant || "emerald";
  return (
    <div
      className={`relative overflow-hidden rounded-xl border bg-white p-5 shadow-sm`}
      data-testid="ad-slot-preview"
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${VARIANT_RAIL[variant]}`} aria-hidden="true" />
      <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <Megaphone className="w-5 h-5 mt-0.5 shrink-0 text-slate-600" />
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-flex items-center rounded-full ${VARIANT_TAG[variant]} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide`}>
              {t("referrals.promoSlots.internalNotice")}
            </span>
          </div>
          <h3 className="text-base font-semibold text-slate-900" data-testid="ad-slot-preview-title">
            {title || <em className="text-slate-400">{t("referrals.adminAdSlots.preview.emptyHint")}</em>}
          </h3>
          <p className="text-sm text-slate-600 mt-0.5" data-testid="ad-slot-preview-body">{body}</p>
        </div>
        {cta && (
          <button
            type="button"
            disabled
            className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-white shrink-0 ${VARIANT_CTA[variant]} opacity-90`}
            data-testid="ad-slot-preview-cta"
          >
            {cta}
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// A19-P6.6 — compact daily time-series panel with sparkline bars + table.
const TimeseriesPanel = ({ data, loading, t }) => {
  if (loading) {
    return (
      <div className="text-xs text-slate-500 py-2" data-testid="ad-slot-timeseries-loading">
        {t("referrals.adminAdSlots.timeseries.loading")}
      </div>
    );
  }
  if (!data || !Array.isArray(data.series) || data.series.length === 0) {
    return (
      <div className="text-xs text-slate-500 py-2" data-testid="ad-slot-timeseries-empty">
        {t("referrals.adminAdSlots.timeseries.empty")}
      </div>
    );
  }
  const maxImp = Math.max(1, ...data.series.map((d) => d.impressions || 0));
  const totalImp = data.series.reduce((acc, d) => acc + (d.impressions || 0), 0);
  const totalClk = data.series.reduce((acc, d) => acc + (d.clicks || 0), 0);
  const totalCtr = totalImp > 0 ? (totalClk / totalImp) : 0;
  return (
    <div className="space-y-3" data-testid="ad-slot-timeseries-panel">
      {/* Sparkline-style bar chart */}
      <div className="flex items-end gap-1 h-24" aria-hidden="true">
        {data.series.map((d, i) => {
          const ratio = (d.impressions || 0) / maxImp;
          const heightPct = Math.max(2, Math.round(ratio * 100));
          const dateLabel = (() => {
            try {
              return new Intl.DateTimeFormat(undefined, { month: "short", day: "2-digit" }).format(new Date(d.date));
            } catch { return d.date; }
          })();
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
              <div
                className="w-full rounded-t bg-emerald-500/80 hover:bg-emerald-600 transition-all"
                style={{ height: `${heightPct}%` }}
                title={`${dateLabel}: ${d.impressions} ${t("referrals.adminAdSlots.table.impressions")} · ${d.clicks} ${t("referrals.adminAdSlots.table.clicks")}`}
                data-testid={`ad-slot-timeseries-bar-${i}`}
              />
              <span className="text-[9px] text-slate-500 leading-none truncate w-full text-center">
                {dateLabel.replace(/\s/, "")}
              </span>
            </div>
          );
        })}
      </div>
      {/* Totals strip */}
      <div className="flex flex-wrap gap-4 text-xs">
        <span className="text-slate-600">
          {t("referrals.adminAdSlots.timeseries.totalImpressions")}: <strong className="text-slate-900 tabular-nums">{totalImp}</strong>
        </span>
        <span className="text-slate-600">
          {t("referrals.adminAdSlots.timeseries.totalClicks")}: <strong className="text-slate-900 tabular-nums">{totalClk}</strong>
        </span>
        <span className="text-slate-600">
          {t("referrals.adminAdSlots.timeseries.avgCtr")}: <strong className="text-slate-900 tabular-nums">{totalImp > 0 ? `${(totalCtr * 100).toFixed(1)}%` : "—"}</strong>
        </span>
      </div>
      {/* Compact daily table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="ad-slot-timeseries-table">
          <thead>
            <tr className="text-start border-b text-slate-500 uppercase tracking-wide text-[10px]">
              <th className="py-1 pe-3">{t("referrals.adminAdSlots.timeseries.colDate")}</th>
              <th className="py-1 pe-3 text-end">{t("referrals.adminAdSlots.table.impressions")}</th>
              <th className="py-1 pe-3 text-end">{t("referrals.adminAdSlots.table.clicks")}</th>
              <th className="py-1 pe-3 text-end">{t("referrals.adminAdSlots.table.ctr")}</th>
            </tr>
          </thead>
          <tbody>
            {data.series.map((d) => (
              <tr key={d.date} className="border-b last:border-0">
                <td className="py-1 pe-3 text-slate-700">{d.date}</td>
                <td className="py-1 pe-3 text-end tabular-nums">{d.impressions}</td>
                <td className="py-1 pe-3 text-end tabular-nums">{d.clicks}</td>
                <td className="py-1 pe-3 text-end tabular-nums">
                  {d.impressions > 0 ? `${(d.ctr * 100).toFixed(1)}%` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const AdminAdSlots = () => {
  const { t, tCountry } = useLang();
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});  // {slot_id: {impressions, clicks, ctr}}
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    placement: "", target_profile: "", target_country: "", active_only: false,
  });
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data || [])).catch(() => setCountries([]));
  }, []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);   // null = create
  const [editingSlotId, setEditingSlotId] = useState(null);  // business slot_id for stats
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  // A19-P6.6 — time-series of the slot currently in the drawer.
  const [timeseries, setTimeseries] = useState(null);
  const [tsLoading, setTsLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.placement) params.placement = filters.placement;
      if (filters.target_profile) params.target_profile = filters.target_profile;
      if (filters.target_country) params.target_country = filters.target_country.toUpperCase();
      if (filters.active_only) params.active_only = true;
      const [list, kpiRes] = await Promise.all([
        api.get("/admin/ad-slots", { params }),
        api.get("/admin/ad-slots/kpis").catch(() => ({ data: { kpis: {} } })),
      ]);
      setRows(list.data?.slots || []);
      setKpis(kpiRes.data?.kpis || {});
    } catch (err) {
      toast.error(extractErrorMessage(err, "load_failed"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditingId(null);
    setEditingSlotId(null);
    setTimeseries(null);
    setForm(emptyForm());
    setDrawerOpen(true);
  };
  const openEdit = (slot) => {
    setEditingId(slot.id);
    setEditingSlotId(slot.slot_id);
    setTimeseries(null);
    setForm({
      slot_id: slot.slot_id,
      placement: slot.placement,
      title_text: slot.title_text || "",
      title_key:  slot.title_key  || "",
      body_text:  slot.body_text  || "",
      body_key:   slot.body_key   || "",
      cta_text:   slot.cta_text   || "",
      cta_key:    slot.cta_key    || "",
      cta_target: slot.cta_target,
      style_variant: slot.style_variant,
      target_profile: slot.target_profile || "",
      target_country: slot.target_country || "",
      target_language: slot.target_language || "",
      priority: slot.priority,
      starts_at: isoToLocal(slot.starts_at),
      ends_at: isoToLocal(slot.ends_at),
      active: !!slot.active,
    });
    setDrawerOpen(true);
    // A19-P6.6 — fetch time-series for this slot.
    setTsLoading(true);
    api
      .get(`/admin/ad-slots/by-slot-id/${encodeURIComponent(slot.slot_id)}/timeseries`, { params: { days: 7 } })
      .then((r) => setTimeseries(r.data))
      .catch(() => setTimeseries(null))
      .finally(() => setTsLoading(false));
  };

  const submitForm = async () => {
    // POST-DEPLOY-FIX-03 — date sanity (mirrors backend; surfaces inline
    // instead of a 400 toast). Allow editing past records.
    if (!editingId && form.starts_at) {
      const startMs = new Date(form.starts_at).getTime();
      if (Number.isFinite(startMs) && startMs < Date.now() - 60_000) {
        toast.error(t("calendar.toasts.startInPast"));
        return;
      }
    }
    if (form.starts_at && form.ends_at && form.ends_at <= form.starts_at) {
      toast.error(t("calendar.toasts.endBeforeStart"));
      return;
    }
    setSaving(true);
    const payload = {
      slot_id: form.slot_id.trim().toLowerCase(),
      placement: form.placement,
      title_text: form.title_text.trim() || null,
      title_key:  form.title_key.trim()  || null,
      body_text:  form.body_text.trim()  || null,
      body_key:   form.body_key.trim()   || null,
      cta_text:   form.cta_text.trim()   || null,
      cta_key:    form.cta_key.trim()    || null,
      cta_target: form.cta_target,
      style_variant: form.style_variant,
      target_profile: form.target_profile || null,
      target_country: form.target_country.trim().toUpperCase() || null,
      target_language: form.target_language.trim().toLowerCase() || null,
      priority: Number(form.priority) || 0,
      starts_at: localToIso(form.starts_at),
      ends_at: localToIso(form.ends_at),
      active: !!form.active,
    };
    try {
      if (editingId) {
        await api.put(`/admin/ad-slots/${editingId}`, payload);
        toast.success(t("referrals.adminAdSlots.toasts.updated"));
      } else {
        await api.post("/admin/ad-slots", payload);
        toast.success(t("referrals.adminAdSlots.toasts.created"));
      }
      setDrawerOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("referrals.adminAdSlots.toasts.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (slot) => {
    try {
      await api.put(`/admin/ad-slots/${slot.id}`, { active: !slot.active });
      toast.success(t("referrals.adminAdSlots.toasts.updated"));
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("referrals.adminAdSlots.toasts.saveFailed")));
    }
  };

  const deleteSlot = async (slot) => {
    if (!window.confirm(t("referrals.adminAdSlots.confirmDelete"))) return;
    try {
      await api.delete(`/admin/ad-slots/${slot.id}`);
      toast.success(t("referrals.adminAdSlots.toasts.deleted"));
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("referrals.adminAdSlots.toasts.saveFailed")));
    }
  };

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (b.priority || 0) - (a.priority || 0)),
    [rows],
  );

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="admin-ad-slots-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="admin-ad-slots-title">
            <Megaphone className="w-6 h-6 text-emerald-700" />
            {t("referrals.adminAdSlots.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">{t("referrals.adminAdSlots.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={fetchAll} disabled={loading}
            data-testid="admin-ad-slots-refresh"
          >
            <RefreshCw className="w-4 h-4 me-1" />
            {t("referrals.adminAdSlots.refresh")}
          </Button>
          <Button
            size="sm"
            onClick={openCreate}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="admin-ad-slots-create-btn"
          >
            <Plus className="w-4 h-4 me-1" />
            {t("referrals.adminAdSlots.create")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.filters.placement")}</span>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={filters.placement}
              onChange={(e) => setFilters((f) => ({ ...f, placement: e.target.value }))}
              data-testid="admin-ad-slots-filter-placement"
            >
              <option value="">{t("referrals.adminAdSlots.filters.all")}</option>
              {PLACEMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.filters.profile")}</span>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={filters.target_profile}
              onChange={(e) => setFilters((f) => ({ ...f, target_profile: e.target.value }))}
              data-testid="admin-ad-slots-filter-profile"
            >
              {PROFILE_OPTIONS.map((p) => (
                <option key={p || "any"} value={p}>{p ? (t(`profiles.${p}`) || p) : t("referrals.adminAdSlots.filters.all")}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.filters.country")}</span>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={filters.target_country}
              onChange={(e) => setFilters((f) => ({ ...f, target_country: e.target.value }))}
              data-testid="admin-ad-slots-filter-country"
            >
              <option value="">{t("referrals.adminAdSlots.filters.all")}</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>{`${c.code} — ${tCountry(c.code, c)}`}</option>
              ))}
            </select>
          </label>
          <label className="text-sm flex items-end gap-2">
            <input
              type="checkbox"
              checked={filters.active_only}
              onChange={(e) => setFilters((f) => ({ ...f, active_only: e.target.checked }))}
              data-testid="admin-ad-slots-filter-active"
            />
            {t("referrals.adminAdSlots.filters.activeOnly")}
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{sortedRows.length} {t("referrals.adminAdSlots.title")}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {sortedRows.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="admin-ad-slots-empty">
              {t("referrals.adminAdSlots.table.empty")}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-start border-b text-slate-600 text-xs uppercase">
                  <th className="py-2 pe-3">{t("referrals.adminAdSlots.table.slotId")}</th>
                  <th className="py-2 pe-3">{t("referrals.adminAdSlots.table.placement")}</th>
                  <th className="py-2 pe-3">{t("referrals.adminAdSlots.table.priority")}</th>
                  <th className="py-2 pe-3">{t("referrals.adminAdSlots.table.profile")}</th>
                  <th className="py-2 pe-3">{t("referrals.adminAdSlots.table.country")}</th>
                  <th className="py-2 pe-3">{t("referrals.adminAdSlots.table.status")}</th>
                  <th className="py-2 pe-3 text-end">{t("referrals.adminAdSlots.table.impressions")}</th>
                  <th className="py-2 pe-3 text-end">{t("referrals.adminAdSlots.table.clicks")}</th>
                  <th className="py-2 pe-3 text-end">{t("referrals.adminAdSlots.table.ctr")}</th>
                  <th className="py-2 pe-3">{t("referrals.adminAdSlots.table.window")}</th>
                  <th className="py-2 pe-3 text-end">{t("referrals.adminAdSlots.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((s) => {
                  const st = slotStatus(s, t);
                  const k = kpis[s.slot_id] || { impressions: 0, clicks: 0, ctr: 0 };
                  return (
                    <tr key={s.id} className="border-b last:border-0" data-testid={`admin-ad-slot-row-${s.slot_id}`}>
                      <td className="py-2 pe-3 font-mono text-xs">{s.slot_id}</td>
                      <td className="py-2 pe-3">{s.placement}</td>
                      <td className="py-2 pe-3 tabular-nums">{s.priority}</td>
                      <td className="py-2 pe-3">{s.target_profile || "—"}</td>
                      <td className="py-2 pe-3">{s.target_country || "—"}</td>
                      <td className="py-2 pe-3">
                        <Badge className={`${st.cls} border-0`}>{st.label}</Badge>
                      </td>
                      <td className="py-2 pe-3 text-end tabular-nums" data-testid={`admin-ad-slot-impressions-${s.slot_id}`}>{k.impressions}</td>
                      <td className="py-2 pe-3 text-end tabular-nums" data-testid={`admin-ad-slot-clicks-${s.slot_id}`}>{k.clicks}</td>
                      <td className="py-2 pe-3 text-end tabular-nums" data-testid={`admin-ad-slot-ctr-${s.slot_id}`}>
                        {k.impressions > 0 ? `${(k.ctr * 100).toFixed(1)}%` : "—"}
                      </td>
                      <td className="py-2 pe-3 text-xs text-slate-600">
                        {fmtDate(s.starts_at)} → {fmtDate(s.ends_at)}
                      </td>
                      <td className="py-2 pe-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`admin-ad-slot-edit-${s.slot_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleActive(s)}
                            data-testid={`admin-ad-slot-toggle-${s.slot_id}`}
                            title={s.active ? t("referrals.adminAdSlots.deactivate") : t("referrals.adminAdSlots.activate")}
                          >
                            {s.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteSlot(s)} data-testid={`admin-ad-slot-delete-${s.slot_id}`}>
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Drawer: create / edit (centered modal) */}
      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent
          className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="admin-ad-slots-drawer"
        >
          <DialogHeader>
            <DialogTitle>{t("referrals.adminAdSlots.form.heading")}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {t("referrals.adminAdSlots.form.hint")}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {/* Preview */}
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                {t("referrals.adminAdSlots.preview.heading")}
              </div>
              <SlotPreview form={form} t={t} />
            </div>

            {/* A19-P6.6 — time-series panel only when editing an existing slot. */}
            {editingId && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid="ad-slot-timeseries-wrapper">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  {t("referrals.adminAdSlots.timeseries.heading", { days: 7 })}
                </div>
                <TimeseriesPanel data={timeseries} loading={tsLoading} t={t} />
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.slotIdLabel")} *</span>
                <Input
                  value={form.slot_id}
                  disabled={!!editingId}
                  onChange={(e) => setForm({ ...form, slot_id: e.target.value })}
                  placeholder="e.g. winter_invite_es"
                  data-testid="admin-ad-slot-form-slot-id"
                />
                <span className="block text-[11px] text-slate-500 mt-1">{t("referrals.adminAdSlots.form.slotIdHelp")}</span>
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.placementLabel")} *</span>
                <select
                  className="w-full border rounded px-2 py-2 text-sm"
                  value={form.placement}
                  onChange={(e) => setForm({ ...form, placement: e.target.value })}
                  data-testid="admin-ad-slot-form-placement"
                >
                  {PLACEMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.titleTextLabel")}</span>
                <Input value={form.title_text} onChange={(e) => setForm({ ...form, title_text: e.target.value })} data-testid="admin-ad-slot-form-title-text" />
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.titleKeyLabel")}</span>
                <Input value={form.title_key} onChange={(e) => setForm({ ...form, title_key: e.target.value })} placeholder="e.g. referrals.promoSlots.inviteGeneral.title" data-testid="admin-ad-slot-form-title-key" />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.bodyTextLabel")}</span>
                <Textarea rows={2} value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} data-testid="admin-ad-slot-form-body-text" />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.bodyKeyLabel")}</span>
                <Input value={form.body_key} onChange={(e) => setForm({ ...form, body_key: e.target.value })} data-testid="admin-ad-slot-form-body-key" />
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.ctaTextLabel")}</span>
                <Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} data-testid="admin-ad-slot-form-cta-text" />
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.ctaKeyLabel")}</span>
                <Input value={form.cta_key} onChange={(e) => setForm({ ...form, cta_key: e.target.value })} data-testid="admin-ad-slot-form-cta-key" />
              </label>
            </div>

            <p className="text-[11px] text-slate-500">{t("referrals.adminAdSlots.form.keyHelp")}</p>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.ctaTargetLabel")}</span>
                <select
                  className="w-full border rounded px-2 py-2 text-sm"
                  value={form.cta_target}
                  onChange={(e) => setForm({ ...form, cta_target: e.target.value })}
                  data-testid="admin-ad-slot-form-cta-target"
                >
                  {CTA_TARGETS.map((c) => <option key={c} value={c}>{t(`referrals.adminAdSlots.cta.${c}`) || c}</option>)}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.styleVariantLabel")}</span>
                <select
                  className="w-full border rounded px-2 py-2 text-sm"
                  value={form.style_variant}
                  onChange={(e) => setForm({ ...form, style_variant: e.target.value })}
                  data-testid="admin-ad-slot-form-style"
                >
                  {STYLE_VARIANTS.map((v) => <option key={v} value={v}>{t(`referrals.adminAdSlots.style.${v}`) || v}</option>)}
                </select>
              </label>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.targetProfileLabel")}</span>
                <select
                  className="w-full border rounded px-2 py-2 text-sm"
                  value={form.target_profile}
                  onChange={(e) => setForm({ ...form, target_profile: e.target.value })}
                  data-testid="admin-ad-slot-form-target-profile"
                >
                  {PROFILE_OPTIONS.map((p) => <option key={p || "any"} value={p}>{p ? (t(`profiles.${p}`) || p) : "—"}</option>)}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.targetCountryLabel")}</span>
                <select
                  className="w-full border rounded px-2 py-2 text-sm"
                  value={form.target_country}
                  onChange={(e) => {
                    const next = e.target.value;
                    // POST-DEPLOY-FIX-03 — suggest the language whose code
                    // most-likely matches the chosen country (ES→es,
                    // IT→it, FR→fr, DE→de, BR/PT→pt-br, JP→ja, …). Only
                    // pre-fills when the admin hasn't picked a language.
                    const codeMap = { ES:"es", IT:"it", FR:"fr", DE:"de", GB:"en", US:"en", IE:"en", BR:"pt-br", PT:"pt-br", JP:"ja", KR:"ko", CN:"zh", RU:"ru", CZ:"cs", NL:"nl", IN:"hi" };
                    setForm((f) => ({
                      ...f,
                      target_country: next,
                      target_language: f.target_language || codeMap[next] || "",
                    }));
                  }}
                  data-testid="admin-ad-slot-form-target-country"
                >
                  <option value="">{`— ${t("referrals.adminAdSlots.filters.all")} —`}</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>{`${c.code} — ${tCountry(c.code, c)}`}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.targetLanguageLabel")}</span>
                <select
                  className="w-full border rounded px-2 py-2 text-sm"
                  value={form.target_language}
                  onChange={(e) => setForm({ ...form, target_language: e.target.value })}
                  data-testid="admin-ad-slot-form-target-language"
                >
                  <option value="">{`— ${t("referrals.adminAdSlots.filters.all")} —`}</option>
                  {LANGUAGES.filter((Lg) => Lg.isActive).map((Lg) => (
                    <option key={Lg.code} value={Lg.code}>{`${Lg.code.toUpperCase()} — ${Lg.nameNative}`}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.priorityLabel")}</span>
                <Input type="number" min={0} max={1000} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="admin-ad-slot-form-priority" />
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.startsAtLabel")} *</span>
                <Input
                  type="datetime-local"
                  value={form.starts_at}
                  min={!editingId ? new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : undefined}
                  onChange={(e) => {
                    const next = e.target.value;
                    setForm((f) => {
                      if (f.ends_at && next && f.ends_at <= next) {
                        // Auto-shift end +1h after the new start so the
                        // saved record never carries an invalid range.
                        const startD = new Date(next);
                        const newEnd = new Date(startD.getTime() + 60 * 60 * 1000);
                        const pad = (n) => String(n).padStart(2, "0");
                        const local = `${newEnd.getFullYear()}-${pad(newEnd.getMonth()+1)}-${pad(newEnd.getDate())}T${pad(newEnd.getHours())}:${pad(newEnd.getMinutes())}`;
                        return { ...f, starts_at: next, ends_at: local };
                      }
                      return { ...f, starts_at: next };
                    });
                  }}
                  data-testid="admin-ad-slot-form-starts"
                />
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">{t("referrals.adminAdSlots.form.endsAtLabel")} *</span>
                <Input
                  type="datetime-local"
                  value={form.ends_at}
                  min={form.starts_at || undefined}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                  aria-invalid={form.starts_at && form.ends_at && form.ends_at <= form.starts_at}
                  data-testid="admin-ad-slot-form-ends"
                />
              </label>
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox" checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                data-testid="admin-ad-slot-form-active"
              />
              {t("referrals.adminAdSlots.form.activeLabel")}
            </label>

            <p className="text-[11px] text-slate-500">{t("referrals.adminAdSlots.form.hint")}</p>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDrawerOpen(false)} data-testid="admin-ad-slot-form-cancel">
                {t("referrals.adminAdSlots.cancel")}
              </Button>
              <Button
                onClick={submitForm}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="admin-ad-slot-form-save"
              >
                {t("referrals.adminAdSlots.save")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAdSlots;
